import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  Alert,
  Button,
  IconButton,
  Stack,
  AlertTitle, Typography,
} from '@mui/material';
import api from '../../../services/api';
import { useStateContext } from '../../../hooks/useStateContext';
import Snackbar from '@mui/material/Snackbar';

import Box from '@mui/material/Box';
import { DataGridPro } from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import { SlidingPaneAddNew } from '../../../components/Page/SlidingPaneAddNew';
import { SlidingPaneEdit } from '../../../components/Page/SlidingPaneEdit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import {
  Calendar,
  dateFnsLocalizer,
  Navigate,
} from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import enGB from 'date-fns/locale/en-GB';

import { format, parse, startOfWeek, getDay } from 'date-fns';
import { guideHaveTimeStaff } from '../../../schema/guideHaveTimeStaff';

import PropTypes from 'prop-types';
import CustomToolbar from '../../../components/Sceduler/CustomToolbar';

const SchedulerPage = () => {
  const { user } = useStateContext();
  const [state, setState] = React.useState(false);
  const [addNewEntity, setAddNewEntity] = React.useState({
    open: false,
    data: {},
  });
  const [openedSlidingPane, setOpenedSlidingPane] = useState(undefined);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [stateError, setStateError] = React.useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState(undefined);
  const [openViewDialog, setOpenViewDialog] = React.useState(undefined);
  const [eventsData, setEventsData] = useState(undefined);

  const fetchGuideHaveTime = useCallback(() => {
    const searchParams = new URLSearchParams({});
    setLoading(true);
    api.get(`/staff/${user.id}/guide-have-time`, searchParams).then((res) => {
      const events = res.map((event) => {
        return {
          ...event,
          start: new Date(event.start),
          end: new Date(event.end),
        };
      });

      setEventsData(events);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    fetchGuideHaveTime();
  }, []);

  const onSubmit = useCallback(async () => {
    await api
      .post('/staff/guide-have-times', {})
      .then((result) => {
        setState(true);
      })
      .catch((error) => {
        setStateError(true);
      });
  }, []);

  const handleClose = () => {
    setState(false);
  };

  const handleDeleteClick = (row) => {
    setOpenConfirmDialog(row);
  };

  const handleEditClick = (row) => {
    setOpenedSlidingPane(row);
  };

  const handleCloseError = () => {
    setStateError(false);
  };

  const handleSelect = (props) => {
    setAddNewEntity({
      open: true,
      data: {
        from: dayjs(props?.start).format('YYYY-MM-DD HH:mm'),
        to: dayjs(props?.end).format('YYYY-MM-DD HH:mm'),
      },
    });
  };
  const handleSelectEvent = (props) => {
    setOpenViewDialog({
      from: dayjs(props?.start).format('YYYY-MM-DD HH:mm'),
      to: dayjs(props?.end).format('YYYY-MM-DD HH:mm'),
      id: props?.id,
    });
  };

  const locales = {
    'en-GB': enGB,
  };

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
    getDay,
    locales,
  });

  const columns = React.useMemo(() => {
    return [
      {
        id: 1,
        field: 'start',
        headerName: 'From',
        editable: false,
        type: 'text',
        minWidth: 200,
        renderCell: (props) => {
          const formattedDate = format(props?.value, 'yyyy-MM-dd HH:mm');
          return <>{formattedDate}</>;
        },
      },
      {
        id: 2,
        field: 'end',
        headerName: 'To',
        editable: false,
        type: 'text',
        filterable: true,
        minWidth: 200,
        renderCell: (props) => {
          const formattedDate = format(props?.value, 'yyyy-MM-dd HH:mm');
          return <>{formattedDate}</>;
        },
      },
      {
        id: 5,
        field: 'actions',
        type: 'text',
        headerName: '',
        cellClassName: '',
        editable: false,
        sortable: false,
        width: 100,
        renderCell: (props) => {
          const id = props.id;

          return (
            <>
              <Stack direction={'row'}>
                <IconButton>
                  <EditIcon
                    color={'primary'}
                    onClick={() => {
                      setOpenedSlidingPane(props?.row);
                    }}
                  />
                </IconButton>
                <IconButton>
                  <DeleteIcon
                    color={'error'}
                    onClick={() => {
                      handleDeleteClick({
                        id: props?.row?.id,
                        from: dayjs(props?.row?.start).format(
                          'YYYY-MM-DD HH:mm'
                        ),
                        to: dayjs(props?.row?.end).format('YYYY-MM-DD HH:mm'),
                      });
                    }}
                  />
                </IconButton>
              </Stack>
            </>
          );
        },
      },
    ];
  }, []);

  function CustomAgendaView({
    date,
    events,
    localizer,
    max = localizer.endOf(new Date(), 'day'),
    min = localizer.startOf(new Date(), 'day'),
    scrollToTime = localizer.startOf(new Date(), 'day'),
    ...props
  }) {
    return (
      <DataGridPro
        rows={events}
        columns={columns}
        rowHeight={38}
        disableRowSelectionOnClick
        getRowClassName={(params) => {
          const color =
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
          return `${color}`;
        }}
        disableVirtualization={true}
      />
    );
  }

  CustomAgendaView.propTypes = {
    date: PropTypes.instanceOf(Date).isRequired,
    localizer: PropTypes.object,
    max: PropTypes.instanceOf(Date),
    min: PropTypes.instanceOf(Date),
    scrollToTime: PropTypes.instanceOf(Date),
  };

  CustomAgendaView.range = (date, { localizer }) => {
    const start = date;
    const end = localizer.add(start, 6, 'day');

    let current = start;
    const range = [];

    while (localizer.lte(current, end, 'day')) {
      range.push(current);
      current = localizer.add(current, 1, 'day');
    }

    return range;
  };

  CustomAgendaView.navigate = (date, action, { localizer }) => {
    switch (action) {
      case Navigate.PREVIOUS:
        return localizer.add(date, -3, 'day');

      case Navigate.NEXT:
        return localizer.add(date, 3, 'day');

      default:
        return date;
    }
  };

  CustomAgendaView.title = (date, { localizer }) => {
    const [start, ...rest] = CustomAgendaView.range(date, { localizer });
    return localizer.format({ start, end: rest.pop() }, 'dayRangeHeaderFormat');
  };

  return (
    <>
      <Box
        sx={{
          p: '10px',
          m: '10px',
          borderRadius: '10px',
          background: '#fff',
        }}
      >
        <Alert
          severity="info"
          sx={{
            background: '#f3f3f3',
            color: '#4f4f4f',
            '& .MuiAlert-icon': {
              color: '#dc2cd7',
            },
          }}
        >
          <AlertTitle>Availability - <Typography variant={'caption'}>Here you can specify the times you are available to work</Typography></AlertTitle>
        </Alert>

        <Stack
          sx={{
            my: '10px',
            position: {
              xs: 'relative',
              md: 'absolute',
            },
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setAddNewEntity({
                open: true,
                data: {},
              });
            }}
            color={'secondary'}
            sx={{
              maxWidth: {
                xs: '100%',
                md: '150px',
              },
            }}
          >
            Add New
          </Button>
        </Stack>

        {eventsData ? (
          <Calendar
            timeslots={4}
            step={15}
            views={{
              agenda: CustomAgendaView,
              day: true,
              week: true,
              month: true,
            }}
            selectable
            localizer={localizer}
            defaultView="week"
            defaultDate={new Date()}
            events={eventsData}
            style={{ height: 'calc(100vh - 180px)' }}
            onSelectEvent={(event) => handleSelectEvent(event)}
            onSelectSlot={handleSelect}
            messages={null}
            culture={'en-GB'}
            components={{
              toolbar: CustomToolbar,
            }}
          />
        ) : (
          <>
            <Box>
              <CircularProgress />
            </Box>
          </>
        )}
      </Box>

      {addNewEntity.open && (
        <SlidingPaneAddNew
          schema={guideHaveTimeStaff}
          id={undefined}
          onSubmit={() => {
            fetchGuideHaveTime();
            setAddNewEntity({
              open: false,
              data: {},
            });
          }}
          propertiesFromTable={addNewEntity.data}
          onClose={(value) => {
            setAddNewEntity({
              open: false,
              data: {},
            });
          }}
        />
      )}

      {openedSlidingPane && (
        <SlidingPaneEdit
          schema={guideHaveTimeStaff}
          id={openedSlidingPane?.id}
          onSubmit={() => {
            fetchGuideHaveTime();
            setOpenedSlidingPane(undefined);
            setOpenViewDialog(undefined);
          }}
          propertiesFromTable={openedSlidingPane}
          onClose={(value) => {
            setOpenedSlidingPane(undefined);
          }}
        />
      )}

      {openConfirmDialog && (
        <>
          <Dialog
            open={true}
            onClose={() => {
              setOpenConfirmDialog(undefined);
            }}
          >
            <DialogTitle>Delete Work Time</DialogTitle>
            <DialogContent
              sx={{
                fontSize: {
                  xs: '14px',
                },
              }}
            >
              {`${openConfirmDialog.from} - ${openConfirmDialog.to}`}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenConfirmDialog(undefined);
                }}
              >
                Cancel
              </Button>
              <Button
                variant={'contained'}
                color={'error'}
                onClick={() => {
                  guideHaveTimeStaff.client
                    .delete(openConfirmDialog.id, {})
                    .then((res) => {
                      setState(true);
                      setOpenConfirmDialog(undefined);
                      fetchGuideHaveTime();
                    })
                    .catch((e) => {
                      setStateError(true);
                    });
                }}
                autoFocus
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      {openViewDialog && (
        <>
          <Dialog
            open={true}
            onClose={() => {
              setOpenViewDialog(undefined);
            }}
          >
            <DialogTitle>Work Time</DialogTitle>
            <DialogContent
              sx={{
                fontSize: {
                  xs: '14px',
                },
              }}
            >
              {`${openViewDialog?.from} - ${openViewDialog?.to}`}
            </DialogContent>
            <DialogActions>
              <IconButton
                onClick={() => {
                  guideHaveTimeStaff.client
                    .delete(openViewDialog.id, {})
                    .then((res) => {
                      setState(true);
                      setOpenViewDialog(undefined);
                      fetchGuideHaveTime();
                    })
                    .catch((e) => {
                      setStateError(true);
                    });
                }}
              >
                <DeleteIcon color={'error'} />
              </IconButton>
              <IconButton
                onClick={() => {
                  setOpenedSlidingPane(openViewDialog);
                }}
              >
                <EditIcon color={'primary'} />
              </IconButton>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Snackbar
        open={state}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Successfully deleted
        </Alert>
      </Snackbar>

      <Snackbar
        open={stateError}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Error: unable to delete
        </Alert>
      </Snackbar>
    </>
  );
};

export default SchedulerPage;
